import { useMemo, useState } from 'react';
import { useDebounce, useMount } from 'react-use';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useOrDateTimeState } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks';
import { ResourcesAvailabilityParams } from '@/api/apis/resources';
import { useAircraftAvailability, useDevicesAvailability } from '@/queries/resource/availability';
import { useHubsQuery } from '@/queries/hub';
import { getTimeZonedDate } from '@/utils/helpers';
import {
  useClinicalSpecialistsForAssignment,
  useContractorSurgeonsForAssignment,
  useSurgeonsForAssignment,
} from '@/queries/resource';
import { AircraftMarkerType, MapFilterType, MapMarkerType, RouteBuilderFilterType } from '../types';
import { getDefaultFilters } from '../config';
import { getAircraftMarkers, getHubMarkers, getSurgeonMarkers } from '../utils';

type UseMapFilterType = {
  filters: RouteBuilderFilterType | null;
  updateFilters: (
    filter: RouteBuilderFilterType[keyof RouteBuilderFilterType],
    type: keyof RouteBuilderFilterType,
  ) => void;
  markers: MapMarkerType[];
  aircraftsMarkers: AircraftMarkerType[];
  isLoading: boolean;
};

export const useMapFilter = (): UseMapFilterType => {
  const { caseId, organType, donorType, donorHospitalDate, donorHospitalTime, donorHospitalTimeZone } =
    useCaseDetailsWithReviews();
  const filters = useRouteBuilderState.use.filters();
  const updateFilters = useRouteBuilderState.use.updateFilters();
  const setFilters = useRouteBuilderState.use.setFilters();

  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const { orDateTime } = useOrDateTimeState(orCaseDateTime);

  useMount(() => {
    setFilters(getDefaultFilters({ donorHospitalDate, donorHospitalTime, donorHospitalTimeZone }));
  });

  const [currentFilters, setCurrentFilters] = useState<ResourcesAvailabilityParams & { caseId: string }>({
    caseId,
    reserved: false,
  });

  const { data: hubs } = useHubsQuery();
  const { data: devicesList, isFetching: isDevicesFetching } = useDevicesAvailability({
    caseId,
    reserved: currentFilters.reserved,
  });
  const { data: csCandidates, isFetching: isCsCandidatesFetching } = useClinicalSpecialistsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orDateTime.toAbsoluteString(),
  });
  const { data: sCandidates, isFetching: isSCandidatesFetching } = useSurgeonsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orDateTime.toAbsoluteString(),
  });
  const { data: contractorCandidates, isFetching: isContractorCandidatesFetching } = useContractorSurgeonsForAssignment(
    {
      caseId,
      donor: donorType,
      organ: organType,
      orTime: orDateTime.toAbsoluteString(),
    },
  );
  const { data: aircrafts, isFetching: isAircraftsFetching } = useAircraftAvailability(currentFilters);

  useDebounce(
    () => {
      if (filters) {
        setCurrentFilters({
          caseId,
          from: filters[MapFilterType.from],
          to: filters[MapFilterType.to],
          reserved: filters[MapFilterType.showReserved],
        });
      }
    },
    500,
    [filters, caseId],
  );

  const hubMarkers = useMemo(() => {
    if (!filters) return [];

    return getHubMarkers({
      organType,
      filters,
      hubs: hubs?.items || [],
      csList: csCandidates,
      devicesList,
    });
  }, [filters, organType, hubs?.items, csCandidates, devicesList]);

  const surgeonMarkers = useMemo(() => {
    if (!filters?.surgeon) return [];
    return getSurgeonMarkers(filters, sCandidates, contractorCandidates);
  }, [filters, sCandidates, contractorCandidates]);

  const aircraftsMarkers = useMemo(() => {
    if (!filters?.aircraft) return [];
    return getAircraftMarkers(filters, aircrafts);
  }, [filters, aircrafts]);

  const markers = useMemo(
    () => [...hubMarkers, ...surgeonMarkers, ...aircraftsMarkers],
    [hubMarkers, surgeonMarkers, aircraftsMarkers],
  );

  return {
    markers,
    aircraftsMarkers,
    filters,
    updateFilters,
    isLoading:
      isDevicesFetching ||
      isCsCandidatesFetching ||
      isSCandidatesFetching ||
      isContractorCandidatesFetching ||
      isAircraftsFetching,
  };
};
